import { HeadersFunction, LoaderFunctionArgs, MetaArgs } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import {
	GQLOrderDirection,
	GQLProductStatusType,
	GQLProductTypeType,
	GetBrandsDocument,
	GetCategoriesDocument,
	GetColourGroupsDocument,
	GetOperationalSettingsDocument,
	GetProductCollectionsDocument,
	GetProductListingPageDocument,
	GetProductTilesWithMinMaxDocument,
	GetSizesDocument,
} from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonHour } from '@theprintbar/server';
import { WebRoutes, invariantError } from '@theprintbar/shared';
import { ProductListingPage, getProductSearchVariables } from '@theprintbar/ui';
import { SitemapFunction } from 'remix-sitemap';
import { useRootData } from '~/root';
import { contentMeta } from '~/utils/contentMeta';
import { getProductListingCanonical, getProductListingCanonicalHref } from './components/canonicalUrl';

export const sitemap: SitemapFunction = async ({}) => ({
	loc: WebRoutes.Products(),
	changefreq: 'hourly',
	priority: 1,
});

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const meta = (args: MetaArgs<typeof loader>) => {
	return [...contentMeta({ data: args.data?.productListingPage, matches: args.matches })];
};

export async function loader({ request }: LoaderFunctionArgs) {
	const { params, variables } = getProductSearchVariables({
		request,
		isWeb: true,
		orderBy: [{ inStock: GQLOrderDirection.Desc }, { popularity: GQLOrderDirection.Desc }],
	});

	const [
		{ productListingPage },
		{ products, maxPrice, minPrice, totalProducts },
		{ operationalSettings },
		{ brands },
		{ categories },
		{ productCollections },
		{ colourGroups },
		{ sizes },
	] = await Promise.all([
		runSudoGraphql(GetProductListingPageDocument),
		runSudoGraphql(GetProductTilesWithMinMaxDocument, variables),
		runSudoGraphql(GetOperationalSettingsDocument),
		runSudoGraphql(GetBrandsDocument, {
			where: {
				products: {
					some: {
						status: { equals: GQLProductStatusType.Published },
						availableWeb: { equals: true }, // Only include brands with products available on the web
					},
				},
			},
		}),
		runSudoGraphql(GetCategoriesDocument),
		runSudoGraphql(GetProductCollectionsDocument, { where: {} }),
		runSudoGraphql(GetColourGroupsDocument),
		runSudoGraphql(GetSizesDocument),
	]);
	const productTypes = Object.values(GQLProductTypeType);

	invariantError(productListingPage, 'No product listing page found');
	invariantError(operationalSettings, 'No operational settings found');

	return cachedJsonHour({
		productListingPage,
		initialProducts: products,
		minPrice,
		maxPrice,
		totalProducts,
		brands,
		categories,
		collections: productCollections,
		colours: colourGroups,
		sizes,
		operationalSettings,
		params,
		productTypes,
	});
}

export default function () {
	const data = useLoaderData<typeof loader>();
	const { globalContent } = useRootData();
	const samedayCartWarning = globalContent?.samedayCartWarning;

	return (
		<ProductListingPage {...data} productSearchURL={WebRoutes.API.commerce.products.search} samedayCartWarning={samedayCartWarning} />
	);
}
